/**
 * CrossFileAssociations — configure value-match associations across uploaded
 * CSV files.
 *
 * Each row maps a column in one file to a column in another file, producing an
 * `AssociationMapping` that the backend uses to link records during import.
 *
 * Example: `deals.csv` has an `email` column; `contacts.csv` also has `email`.
 * The user creates a mapping from deals→email to contacts→email so that each
 * deal row gets associated with the contact whose email matches.
 */

import { AlertTriangle, Link01, Plus, Trash01 } from '@untitledui/icons';
import type { Key } from 'react';
import { memo, useCallback, useMemo, useRef } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { IconButton } from '@/lib/base/buttons/button-utility';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { AssociationMatcher } from '@/lib/crm/hooks/useAssociationMatchers';
import type {
  AssociationMapping,
  UploadedFileEntry,
} from '@/lib/csv-import/api';

import { validAssociationTypesForPair } from '../../csv-import-helpers';
import { getEntityTypeLabel } from '../../utils/entityTypeLabels';

// ============================================================================
// Types
// ============================================================================

export interface CrossFileAssociationsProps {
  uploadedFiles: UploadedFileEntry[];
  /** Per-file column lists, keyed by file ID string. */
  fileColumns: Record<string, string[]>;
  /** Current cross-file association mappings. */
  associationMappings: AssociationMapping[];
  /** Called with the full updated list on any change. */
  onAssociationMappingsChange: (mappings: AssociationMapping[]) => void;
  /** All association types for the org (from useAssociationMatchers). */
  associationTypes: AssociationType[];
  /** Kind-aware source/target matchers from useAssociationMatchers. */
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  /**
   * File ids (string form) whose declared type currently emits no importable
   * section — an association endpoint on them would be dropped at submit, so
   * rows referencing them show a warning (and validateMappings blocks Next).
   */
  unlinkableFileIds: ReadonlySet<string>;
}

// ============================================================================
// AssociationRow — one configured cross-file mapping
// ============================================================================

interface AssociationRowProps {
  index: number;
  mapping: AssociationMapping;
  files: UploadedFileEntry[];
  fileColumns: Record<string, string[]>;
  associationTypes: AssociationType[];
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  unlinkableFileIds: ReadonlySet<string>;
  onUpdate: (index: number, mapping: AssociationMapping) => void;
  onRemove: (index: number) => void;
}

const AssociationRow = memo(function AssociationRow({
  index,
  mapping,
  files,
  fileColumns,
  associationTypes,
  matchesSource,
  matchesTarget,
  unlinkableFileIds,
  onUpdate,
  onRemove,
}: AssociationRowProps) {
  // Available files for source / target (exclude the other side's selection)
  const fileItems = useMemo(
    () =>
      files.map(f => ({
        id: String(f.file_id),
        label: f.filename,
      })),
    [files]
  );

  const sourceColumns = useMemo(() => {
    const cols = fileColumns[String(mapping.source_file_id)] ?? [];
    return cols.map(c => ({ id: c, label: c }));
  }, [fileColumns, mapping.source_file_id]);

  const targetColumns = useMemo(() => {
    const cols = fileColumns[String(mapping.target_file_id)] ?? [];
    return cols.map(c => ({ id: c, label: c }));
  }, [fileColumns, mapping.target_file_id]);

  // Resolve the selected endpoint files — their entity types determine valid
  // association types, and their emit state drives the unlinkable warning.
  const sourceFile = useMemo(
    () => files.find(f => String(f.file_id) === String(mapping.source_file_id)),
    [files, mapping.source_file_id]
  );

  const targetFile = useMemo(
    () => files.find(f => String(f.file_id) === String(mapping.target_file_id)),
    [files, mapping.target_file_id]
  );

  const sourceEntityType = sourceFile?.entity_type_name ?? '';
  const targetEntityType = targetFile?.entity_type_name ?? '';

  // Selected endpoint files whose declared type currently emits nothing —
  // this association would be dropped at submit, so warn inline (Next is
  // blocked by validateMappings until the file imports records again).
  const unlinkableEndpoints = useMemo(() => {
    const seen = new Set<string>();
    const result: UploadedFileEntry[] = [];
    for (const file of [sourceFile, targetFile]) {
      const id = file === undefined ? undefined : String(file.file_id);
      if (
        file !== undefined &&
        id !== undefined &&
        unlinkableFileIds.has(id) &&
        !seen.has(id)
      ) {
        seen.add(id);
        result.push(file);
      }
    }
    return result;
  }, [sourceFile, targetFile, unlinkableFileIds]);

  const validTypes = useMemo(() => {
    if (!sourceEntityType || !targetEntityType) {
      return [];
    }
    return validAssociationTypesForPair(
      associationTypes,
      matchesSource,
      matchesTarget,
      sourceEntityType,
      targetEntityType
    );
  }, [
    associationTypes,
    matchesSource,
    matchesTarget,
    sourceEntityType,
    targetEntityType,
  ]);

  const assocTypeItems = useMemo(
    () =>
      validTypes.map(at => ({ id: at.association_type_name, label: at.label })),
    [validTypes]
  );

  // When both files are selected but no association type is chosen yet, and
  // there's exactly one valid type, auto-select it.
  const effectiveAssocType =
    mapping.association_type_name ||
    (validTypes.length === 1 ? validTypes[0].association_type_name : '');

  const handleSourceFileChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      onUpdate(index, {
        ...mapping,
        source_file_id: String(key),
        source_column: '',
        // Clear association type when file changes — entity type may differ
        association_type_name: undefined,
      });
    },
    [index, mapping, onUpdate]
  );

  const handleSourceColumnChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      onUpdate(index, { ...mapping, source_column: String(key) });
    },
    [index, mapping, onUpdate]
  );

  const handleTargetFileChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      onUpdate(index, {
        ...mapping,
        target_file_id: String(key),
        target_column: '',
        association_type_name: undefined,
      });
    },
    [index, mapping, onUpdate]
  );

  const handleTargetColumnChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      onUpdate(index, { ...mapping, target_column: String(key) });
    },
    [index, mapping, onUpdate]
  );

  const handleAssocTypeChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      onUpdate(index, { ...mapping, association_type_name: String(key) });
    },
    [index, mapping, onUpdate]
  );

  const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

  return (
    <div className='rounded-lg border border-border-secondary bg-bg-primary p-4'>
      <div className='flex items-center justify-between mb-3'>
        <span className='text-xs font-medium uppercase tracking-wide text-fg-quaternary'>
          Association {index + 1}
        </span>
        <IconButton
          icon={Trash01}
          size='xs'
          color='tertiary'
          tooltip='Remove association'
          onClick={handleRemove}
        />
      </div>

      {/* Source file + column */}
      <div className='space-y-2'>
        <div className='text-xs font-medium text-fg-tertiary'>From</div>
        <div className='flex gap-2'>
          <Select
            size='sm'
            className='flex-1'
            aria-label='Source file'
            placeholder='Select file'
            value={String(mapping.source_file_id)}
            onChange={handleSourceFileChange}
            items={fileItems}
          >
            {item => <SelectItem {...item} />}
          </Select>
          <Select
            size='sm'
            className='flex-1'
            aria-label='Source column'
            placeholder='Select column'
            value={mapping.source_column}
            onChange={handleSourceColumnChange}
            items={sourceColumns}
            isDisabled={!mapping.source_file_id}
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
      </div>

      {/* Target file + column */}
      <div className='space-y-2 mt-3'>
        <div className='text-xs font-medium text-fg-tertiary'>To</div>
        <div className='flex gap-2'>
          <Select
            size='sm'
            className='flex-1'
            aria-label='Target file'
            placeholder='Select file'
            value={String(mapping.target_file_id)}
            onChange={handleTargetFileChange}
            items={fileItems}
          >
            {item => <SelectItem {...item} />}
          </Select>
          <Select
            size='sm'
            className='flex-1'
            aria-label='Target column'
            placeholder='Select column'
            value={mapping.target_column}
            onChange={handleTargetColumnChange}
            items={targetColumns}
            isDisabled={!mapping.target_file_id}
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
      </div>

      {/* Association type — only shown when the pair has multiple valid types */}
      {validTypes.length > 1 && (
        <div className='space-y-2 mt-3'>
          <div className='text-xs font-medium text-fg-tertiary'>
            Association type
          </div>
          <Select
            size='sm'
            aria-label='Association type'
            placeholder='Select association type'
            value={effectiveAssocType}
            onChange={handleAssocTypeChange}
            items={assocTypeItems}
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
      )}

      {/* Endpoint files that currently import no records of their type — the
          association can't be created until they do, and Next stays blocked. */}
      {unlinkableEndpoints.map(file => {
        const typeLabel = getEntityTypeLabel(file.entity_type_name);
        return (
          <div
            key={String(file.file_id)}
            className='mt-3 flex items-start gap-2'
          >
            <AlertTriangle
              className='mt-0.5 size-4 shrink-0 text-fg-warning-primary'
              aria-hidden='true'
            />
            <p className='text-sm text-fg-warning-primary'>
              {`${file.filename} isn't importing any ${typeLabel} records, so it can't be linked. Map at least one of its columns to a ${typeLabel} property first.`}
            </p>
          </div>
        );
      })}
    </div>
  );
});

// ============================================================================
// Main component
// ============================================================================

export function CrossFileAssociations({
  uploadedFiles,
  fileColumns,
  associationMappings,
  onAssociationMappingsChange,
  associationTypes,
  matchesSource,
  matchesTarget,
  unlinkableFileIds,
}: CrossFileAssociationsProps) {
  // Stable key tracking — each row gets a monotonic ID so React can
  // reconcile correctly when a middle row is removed (index-based keys
  // would attach open-popover or focus state to the wrong survivor).
  const nextKeyRef = useRef(0);
  const rowKeysRef = useRef<string[]>([]);

  // Reconcile keys with current mappings. Our handleAdd/handleRemove
  // update rowKeysRef.current before calling the parent, so by the time
  // we re-render the lengths already match. A mismatch means an external
  // change (parent reset) — extend or truncate to resync.
  const currentKeys = rowKeysRef.current;
  let rowKeys: string[];
  if (currentKeys.length === associationMappings.length) {
    rowKeys = currentKeys;
  } else if (currentKeys.length < associationMappings.length) {
    rowKeys = [
      ...currentKeys,
      ...Array.from(
        { length: associationMappings.length - currentKeys.length },
        () => `assoc-${nextKeyRef.current++}`
      ),
    ];
    rowKeysRef.current = rowKeys;
  } else {
    rowKeys = currentKeys.slice(0, associationMappings.length);
    rowKeysRef.current = rowKeys;
  }

  const handleAdd = useCallback(() => {
    // Seed a blank mapping; the user fills in the file/column selects.
    const blank: AssociationMapping = {
      source_file_id: '',
      source_column: '',
      target_file_id: '',
      target_column: '',
    };
    rowKeysRef.current = [
      ...rowKeysRef.current,
      `assoc-${nextKeyRef.current++}`,
    ];
    onAssociationMappingsChange([...associationMappings, blank]);
  }, [associationMappings, onAssociationMappingsChange]);

  const handleUpdate = useCallback(
    (index: number, mapping: AssociationMapping) => {
      const next = [...associationMappings];
      next[index] = mapping;
      onAssociationMappingsChange(next);
    },
    [associationMappings, onAssociationMappingsChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      rowKeysRef.current = rowKeysRef.current.filter((_, i) => i !== index);
      onAssociationMappingsChange(
        associationMappings.filter((_, i) => i !== index)
      );
    },
    [associationMappings, onAssociationMappingsChange]
  );

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div>
        <div className='flex items-center gap-2 mb-1'>
          <Link01 className='size-5 text-fg-quaternary' aria-hidden='true' />
          <h3 className='text-base font-semibold text-fg-primary'>
            Cross-file associations
          </h3>
        </div>
        <p className='text-sm text-fg-secondary'>
          Link records across your uploaded files by matching column values. For
          example, match the <em>Email</em> column in your deals file to the{' '}
          <em>Email</em> column in your contacts file to associate each deal
          with the right contact.
        </p>
      </div>

      {/* Association rows */}
      {associationMappings.length > 0 && (
        <div className='space-y-3'>
          {associationMappings.map((mapping, i) => (
            <AssociationRow
              key={rowKeys[i]}
              index={i}
              mapping={mapping}
              files={uploadedFiles}
              fileColumns={fileColumns}
              associationTypes={associationTypes}
              matchesSource={matchesSource}
              matchesTarget={matchesTarget}
              unlinkableFileIds={unlinkableFileIds}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {associationMappings.length === 0 && (
        <div className='rounded-lg border border-dashed border-border-secondary py-8 px-4 text-center'>
          <Link01
            className='mx-auto mb-2 size-6 text-fg-quaternary'
            aria-hidden='true'
          />
          <p className='text-sm text-fg-tertiary'>
            No cross-file associations configured.
          </p>
          <p className='mt-1 text-xs text-fg-quaternary'>
            Add one to link records between your files by matching column
            values.
          </p>
        </div>
      )}

      {/* Add button */}
      <Button
        type='button'
        color='secondary'
        size='sm'
        iconLeading={Plus}
        onClick={handleAdd}
      >
        Add association
      </Button>
    </div>
  );
}
